import * as h3 from 'h3-js';
import splitGeoJSON from 'geojson-antimeridian-cut';

/**
 * Clamps a numeric value to a safe range for protobuf varint encoding.
 * Protobuf varints have a practical limit that can cause "Given varint doesn't fit into 10 bytes" errors
 * when values are extremely large. We use Number.MAX_SAFE_INTEGER as a conservative upper bound.
 * @param {number} value - Value to clamp
 * @returns {number} Clamped value within safe range
 */
/**
 * Checks if two numbers are effectively equal using tolerance-based comparison.
 * Handles floating point precision issues by using both absolute and relative comparisons.
 * @param {number} a - First value
 * @param {number} b - Second value
 * @param {number} epsilon - Tolerance for comparison (default: 1e-10)
 * @returns {boolean} True if values are effectively equal
 */
export const areValuesEffectivelyEqual = (a, b, epsilon = 1e-10) => {
  if (a === b) return true; // Fast path for exact equality
  if (typeof a !== 'number' || typeof b !== 'number' || !isFinite(a) || !isFinite(b)) {
    return false;
  }
  const diff = Math.abs(b - a);
  const maxAbs = Math.max(Math.abs(a), Math.abs(b));
  // Use absolute comparison for near-zero values, relative for larger values
  return diff < epsilon || (maxAbs > epsilon && diff / maxAbs < epsilon);
};

const clampToSafeRange = (value) => {
  if (typeof value !== 'number' || !isFinite(value)) {
    return value;
  }
  // Use Number.MAX_SAFE_INTEGER (2^53 - 1) as the safe upper bound for protobuf encoding
  // This prevents varint encoding errors in MapLibre GL JS
  const MAX_SAFE = Number.MAX_SAFE_INTEGER;
  const MIN_SAFE = -Number.MAX_SAFE_INTEGER;

  if (value > MAX_SAFE) {
    console.warn(`Value ${value} exceeds safe range, clamping to ${MAX_SAFE}`);
    return MAX_SAFE;
  }
  if (value < MIN_SAFE) {
    console.warn(`Value ${value} exceeds safe range, clamping to ${MIN_SAFE}`);
    return MIN_SAFE;
  }
  return value;
};

/**
 * Normalize any GeoJSON to a FeatureCollection (after antimeridian split).
 * @param {object} geojson
 * @returns {object|null}
 */
const toFeatureCollection = (geojson) => {
  if (!geojson) return null;
  if (geojson.type === 'FeatureCollection') {
    return geojson;
  }
  if (geojson.type === 'Feature') {
    return { type: 'FeatureCollection', features: [geojson] };
  }
  if (geojson.type && geojson.coordinates) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: geojson }],
    };
  }
  return null;
};

/**
 * Splits geometries that cross the antimeridian so MapLibre renders them correctly.
 * @param {object} geojson
 * @returns {object|null}
 */
export const prepareGeoJSONForMap = (geojson) => {
  const collection = toFeatureCollection(geojson);
  if (!collection?.features?.length) {
    return collection ?? geojson;
  }

  try {
    const cut = splitGeoJSON(collection);
    return toFeatureCollection(cut) ?? collection;
  } catch (err) {
    console.warn('Antimeridian cut failed, using original geometry:', err);
    return collection;
  }
};

const walkCoordinates = (coords, depth, points) => {
  if (depth === 0) {
    if (Array.isArray(coords) && coords.length >= 2) {
      points.push(coords);
    }
    return;
  }
  for (const part of coords) {
    walkCoordinates(part, depth - 1, points);
  }
};

const collectGeoJSONPositions = (geojson) => {
  const points = [];
  const addGeometry = (geometry) => {
    if (!geometry?.type || !geometry.coordinates) return;
    const depthByType = {
      Point: 0,
      MultiPoint: 1,
      LineString: 1,
      MultiLineString: 2,
      Polygon: 2,
      MultiPolygon: 3,
    };
    const depth = depthByType[geometry.type];
    if (depth === undefined) return;
    walkCoordinates(geometry.coordinates, depth, points);
  };

  if (geojson.type === 'FeatureCollection') {
    geojson.features.forEach((feature) => addGeometry(feature.geometry));
  } else if (geojson.type === 'Feature') {
    addGeometry(geojson.geometry);
  } else {
    addGeometry(geojson);
  }

  return points;
};

/**
 * Bounds for fitBounds after antimeridian-safe preparation.
 * @param {object} geojson
 * @returns {[[number, number], [number, number]]|null} SW and NE corners
 */
export const getGeoJSONFitBounds = (geojson) => {
  const prepared = prepareGeoJSONForMap(geojson);
  if (!prepared?.features?.length) {
    return null;
  }

  try {
    const points = collectGeoJSONPositions(prepared);
    if (points.length === 0) {
      return null;
    }

    let lngs = points.map((p) => p[0]);
    const lats = points.map((p) => p[1]);
    let minLng = Math.min(...lngs);
    let maxLng = Math.max(...lngs);

    // Use 0..360 continuity when the naive span wraps the globe (dateline features).
    if (maxLng - minLng > 180) {
      lngs = lngs.map((lng) => (lng < 0 ? lng + 360 : lng));
      minLng = Math.min(...lngs);
      maxLng = Math.max(...lngs);
    }

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    let swLng = minLng;
    let neLng = maxLng;
    if (swLng > 180) swLng -= 360;
    if (neLng > 180) neLng -= 360;

    if (!Number.isFinite(swLng) || !Number.isFinite(minLat)) {
      return null;
    }

    return [[swLng, minLat], [neLng, maxLat]];
  } catch (err) {
    console.warn('Failed to compute GeoJSON bounds:', err);
    return null;
  }
};

/**
 * Converts a list of H3 cell data into a GeoJSON FeatureCollection.
 * @param {Array} items - List of objects with { h3: string, value: number, ... }
 * @returns {Object} GeoJSON FeatureCollection
 */
export const h3ToGeoJSON = (items) => {
  const features = items.map(item => {
    try {
      // Get the vertices of the H3 cell
      const boundary = h3.cellToBoundary(item.h3, true); // true for [lng, lat]

      // h3-js returns [[lng, lat], ...] for GeoJSON-friendly format
      // We need to close the loop: last point = first point
      const coordinates = [...boundary, boundary[0]];

      // Clamp value to safe range to prevent protobuf varint encoding errors
      const safeValue = typeof item.value === 'number' ? clampToSafeRange(item.value) : item.value;

      return {
        type: 'Feature',
        properties: {
          ...item,
          // We include h3 and value explicitly for style functions
          h3: item.h3,
          value: safeValue
        },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      };
    } catch (e) {
      console.warn(`Invalid H3 index: ${item.h3}`, e);
      return null;
    }
  }).filter(f => f !== null);

  return {
    type: 'FeatureCollection',
    features,
  };
};

/**
 * Converts items with pre-computed geometry to GeoJSON FeatureCollection.
 * Used for polygon/point queries where geometry is already in the response.
 * @param {Array} items - List of objects with { geometry: object, value: number, ... }
 * @returns {Object} GeoJSON FeatureCollection
 */
export const itemsToGeoJSON = (items) => {
  const features = items.map(item => {
    // If geometry is already an object, use it directly
    const geometry = item.geometry;

    if (!geometry) {
      console.warn('Item missing geometry field:', item);
      return null;
    }

    // Create feature with all properties except geometry
    const { geometry: _, ...properties } = item;

    // Clamp value to safe range to prevent protobuf varint encoding errors
    const safeValue = typeof item.value === 'number' ? clampToSafeRange(item.value) : item.value;

    return {
      type: 'Feature',
      properties: {
        ...properties,
        value: safeValue // Ensure value is included for styling, clamped to safe range
      },
      geometry: geometry
    };
  }).filter(f => f !== null);

  return {
    type: 'FeatureCollection',
    features,
  };
};

/**
 * Computes equal-count quantiles from actual data values
 * Each quantile contains approximately the same number of data points
 * @param {Object} layer - Layer object with data (GeoJSON) and stats
 * @param {number} numClasses - Number of color classes (default: 6)
 * @returns {Array} numClasses + 1 breakpoint values (min … max)
 */
export const computeEqualCountQuantiles = (layer, numClasses = 6, propertyKey = null) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;

  if (numClasses < 1) {
    return [min, max];
  }

  // Only numeric values are valid quantile stops for MapLibre ``step`` expressions
  const values = extractLayerValues(layer, propertyKey).filter(
    (v) => typeof v === 'number' && !isNaN(v) && isFinite(v)
  );

  // If no values or insufficient data, fall back to equal-interval breakpoints
  if (values.length === 0) {
    const step = (max - min) / numClasses;
    return Array.from({ length: numClasses + 1 }, (_, i) => min + i * step);
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const n = sortedValues.length;

  // numClasses color bands → numClasses + 1 breakpoints with equal counts per band
  return Array.from({ length: numClasses + 1 }, (_, i) => {
    const index = Math.floor((i * (n - 1)) / numClasses);
    return sortedValues[Math.min(Math.max(0, index), n - 1)];
  });
};

export const CLASSIFICATION_METHODS = {
  QUANTILE: 'quantile',
  EQUAL_INTERVAL: 'equal_interval',
  MANUAL: 'manual'
};

export const DEFAULT_NUM_CLASSES = 6;
export const MIN_NUM_CLASSES = 3;
export const MAX_NUM_CLASSES = 9;

export const getLayerNumClasses = (layer) => {
  const n = layer?.config?.numClasses;
  if (typeof n === 'number' && n >= MIN_NUM_CLASSES && n <= MAX_NUM_CLASSES) {
    return Math.round(n);
  }
  return DEFAULT_NUM_CLASSES;
};

/**
 * Equal-width breaks from min to max (same span per class).
 */
export const computeEqualIntervalBreaks = (layer, numClasses = 6) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;
  if (numClasses < 1 || areValuesEffectivelyEqual(min, max)) {
    return [min, max];
  }
  const step = (max - min) / numClasses;
  return Array.from({ length: numClasses + 1 }, (_, i) => min + i * step);
};

/**
 * Normalizes class-break thresholds while preserving class count.
 * Duplicate / out-of-order values are repaired in place — never dropped —
 * so a conflicting manual edit cannot collapse levels.
 */
export const normalizeClassBreaks = (breaks, min, max) => {
  if (!Array.isArray(breaks) || breaks.length < 2) {
    return [min, max];
  }
  const clamped = breaks
    .filter((v) => typeof v === 'number' && isFinite(v))
    .map((v) => Math.max(min, Math.min(max, v)));

  if (clamped.length < 2) {
    return [min, max];
  }

  const sorted = [...clamped].sort((a, b) => a - b);
  sorted[0] = min;
  sorted[sorted.length - 1] = max;

  const span = max - min;
  const eps = span > 0 ? span / (sorted.length * 1e6) : 1e-9;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] <= sorted[i - 1]) {
      sorted[i] = sorted[i - 1] + eps;
    }
  }

  if (sorted[sorted.length - 1] > max) {
    sorted[sorted.length - 1] = max;
    for (let i = sorted.length - 2; i >= 0; i--) {
      if (sorted[i] >= sorted[i + 1]) {
        sorted[i] = sorted[i + 1] - eps;
      }
    }
    sorted[0] = min;
  }

  return sorted;
};

/**
 * Auto-computed breaks for the layer's current classification settings.
 */
export const computeAutoClassBreaks = (layer, numClasses, propertyKey = null) => {
  const method = layer?.config?.classificationMethod ?? CLASSIFICATION_METHODS.QUANTILE;
  if (method === CLASSIFICATION_METHODS.EQUAL_INTERVAL) {
    return computeEqualIntervalBreaks(layer, numClasses);
  }
  return computeEqualCountQuantiles(layer, numClasses, propertyKey);
};

/**
 * Resolves class break thresholds (min … max) for numeric choropleths.
 */
export const resolveClassificationBreaks = (layer, numClasses, propertyKey = null) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;
  const method = layer?.config?.classificationMethod ?? CLASSIFICATION_METHODS.QUANTILE;

  if (method === CLASSIFICATION_METHODS.MANUAL && Array.isArray(layer.config?.classBreaks)) {
    const normalized = normalizeClassBreaks(layer.config.classBreaks, min, max);
    if (normalized.length >= 2) {
      return normalized;
    }
  }

  return computeAutoClassBreaks(layer, numClasses, propertyKey);
};

/** Whether the layer uses a numeric choropleth that supports reclassification. */
export const layerSupportsClassification = (layer) => {
  if (layer?.config?.uniformColor || layer?.config?.highCardinalityCategorical) {
    return false;
  }
  const colorProperty = getLayerColorProperty(layer);
  if (!colorProperty || !layer?.data?.features?.length) {
    return false;
  }
  const uniqueValues = extractUniqueValues(layer, colorProperty);
  if (uniqueValuesNeedMatchExpression(uniqueValues, layer.stats)) {
    return false;
  }
  if (uniqueValues.length > 0 && uniqueValues.length < 10) {
    return false;
  }
  const min = layer.stats?.min;
  const max = layer.stats?.max;
  if (min == null || max == null || areValuesEffectivelyEqual(min, max)) {
    return false;
  }
  return true;
};

/**
 * Gets all values from layer GeoJSON data
 * @param {Object} layer - Layer object with data (GeoJSON)
 * @returns {Array} Array of numeric values
 */
export const isMapMetricValue = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  return typeof val === 'number' && !isNaN(val) && isFinite(val);
};

/** Property keys that are geometry identifiers, not metrics for coloring. */
export const MAP_SPATIAL_PROPERTY_KEYS = new Set(['h3', 'lat', 'lon', 'geometry']);

export const getLayerColorProperty = (layer) => {
  if (layer?.config?.uniformColor) return null;
  return layer?.config?.colorProperty || 'value';
};

export const propertyGetExpression = (propertyKey) => ['get', propertyKey];

/**
 * Keys present on loaded features (dimensions, metric aliases, etc.) usable for coloring.
 */
export const extractRetrievedPropertyKeys = (layer) => {
  const keys = new Set();
  for (const feature of layer?.data?.features || []) {
    if (!feature?.properties) continue;
    for (const key of Object.keys(feature.properties)) {
      if (!MAP_SPATIAL_PROPERTY_KEYS.has(key)) keys.add(key);
    }
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
};

export const layerHasColorableProperty = (layer, propertyKey) => {
  if (!propertyKey || !layer?.data?.features?.length) return false;
  return layer.data.features.some(
    (f) => f.properties && propertyKey in f.properties && isMapMetricValue(f.properties[propertyKey])
  );
};

export const inferPropertyTypeFromLayer = (layer, propertyKey) => {
  const values = extractLayerValues(layer, propertyKey);
  if (values.length === 0) return 'unknown';
  if (values.every((v) => typeof v === 'number')) return 'numeric';
  if (values.some((v) => typeof v === 'string')) return 'categorical';
  return 'numeric';
};

export const computeStatsFromGeoJsonProperty = (geojson, propertyKey) => {
  let minVal = null;
  let maxVal = null;
  let isCategorical = false;

  for (const feature of geojson?.features || []) {
    const raw = feature.properties?.[propertyKey];
    if (!isMapMetricValue(raw)) continue;
    if (typeof raw === 'string') {
      isCategorical = true;
      continue;
    }
    if (typeof raw === 'number') {
      if (minVal === null || raw < minVal) minVal = raw;
      if (maxVal === null || raw > maxVal) maxVal = raw;
    }
  }

  return { min: minVal, max: maxVal, is_categorical: isCategorical };
};

export const extractLayerValues = (layer, propertyKey = null) => {
  if (!layer.data || !layer.data.features) {
    return [];
  }

  const key = propertyKey ?? getLayerColorProperty(layer);
  if (!key) return [];

  return layer.data.features
    .map((feature) => feature.properties?.[key])
    .filter(isMapMetricValue);
};

/**
 * Gets unique values from layer GeoJSON data
 * @param {Object} layer - Layer object with data (GeoJSON)
 * @returns {Array} Array of unique numeric values, sorted
 */
export const extractUniqueValues = (layer, propertyKey = null) => {
  const values = extractLayerValues(layer, propertyKey);
  return [...new Set(values)].sort((a, b) => {
    if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  });
};

/**
 * MapLibre ``match`` expression for discrete categories (strings or numbers).
 * ``step`` only accepts literal numeric thresholds; categorical values must use ``match``.
 */
export const buildCategoryMatchExpression = (categories, defaultColor, propertyKey = 'value') => {
  if (!categories?.length) {
    return defaultColor;
  }
  const expr = ['match', propertyGetExpression(propertyKey)];
  for (const { value, color } of categories) {
    expr.push(value);
    expr.push(color);
  }
  expr.push(defaultColor);
  return expr;
};

/** MapLibre ``step`` needs input, default output, and at least one stop/output pair (5+ elements). */
export const isValidMapLibreStepExpression = (expr) =>
  Array.isArray(expr) && expr[0] === 'step' && expr.length >= 5;

export const uniqueValuesNeedMatchExpression = (uniqueValues, stats) => {
  if (stats?.is_categorical) return true;
  return uniqueValues.some((v) => typeof v === 'string');
};

/** Max distinct categorical labels to assign unique colors; above this, use a neutral fill. */
export const MAP_CATEGORICAL_COLOR_LIMIT = 9;

export const NEUTRAL_HIGH_CARDINALITY_COLOR = '#94a3b8';

export const isHighCardinalityCategorical = (layer, propertyKey = null) => {
  const key = propertyKey ?? getLayerColorProperty(layer);
  if (!key || layer?.config?.uniformColor) return false;
  const uniqueValues = extractUniqueValues(layer, key);
  return (
    uniqueValuesNeedMatchExpression(uniqueValues, layer?.stats) &&
    uniqueValues.length > MAP_CATEGORICAL_COLOR_LIMIT
  );
};

const buildCategoriesFromUniqueValues = (uniqueValues, paletteColors) =>
  uniqueValues.map((value, index) => {
    const colorIndex = uniqueValues.length === 1
      ? Math.floor(paletteColors.length / 2)
      : Math.floor((index / (uniqueValues.length - 1)) * (paletteColors.length - 1));
    const clampedColorIndex = Math.min(colorIndex, paletteColors.length - 1);
    const normalizedValue = typeof value === 'number' ? clampToSafeRange(value) : value;
    return {
      value: normalizedValue,
      color: paletteColors[clampedColorIndex]
    };
  });

/**
 * Computes discrete color bands for a layer
 * Returns the exact same color bands used in both map rendering and legend
 * @param {Object} layer - Layer object with stats and config
 * @param {Function} getPaletteColors - Function to get palette colors
 * @param {Function} mapIntervalToColorIndex - Function to map interval to color index
 * @param {Function} getDefaultPalette - Function to get default palette name
 * @param {number} numClasses - Number of color classes (default: 6)
 * @returns {Object} Object with { quantiles, colorBands, stepExpression }
 */
export const computeDiscreteColorBands = (
  layer,
  getPaletteColors,
  mapIntervalToColorIndex,
  getDefaultPalette,
  numStops = getLayerNumClasses(layer)
) => {
  const colorProperty = getLayerColorProperty(layer);
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;

  const paletteName = layer.config?.palette || getDefaultPalette();
  const isInverted = layer.config?.inverted || false;
  const metricProperty = layer.config?.colorProperty || colorProperty || 'value';

  const uniqueValues = extractUniqueValues(layer, metricProperty);
  const paletteColors = getPaletteColors(paletteName, isInverted);
  const defaultColor = paletteColors?.[0] || '#440154';

  // High-cardinality categorical: too many labels for a meaningful choropleth
  if (
    layer.config?.highCardinalityCategorical ||
    isHighCardinalityCategorical(layer, metricProperty)
  ) {
    return {
      quantiles: uniqueValues,
      colorBands: [],
      stepExpression: NEUTRAL_HIGH_CARDINALITY_COLOR,
      useLinear: false,
      minColor: NEUTRAL_HIGH_CARDINALITY_COLOR,
      maxColor: NEUTRAL_HIGH_CARDINALITY_COLOR,
      isCategorical: true,
      hideColorScale: true,
      highCardinality: true
    };
  }

  if (layer.config?.uniformColor || !colorProperty) {
    const constantColor = paletteColors?.[Math.floor((paletteColors?.length || 1) / 2)] || '#440154';
    return {
      quantiles: [min, max],
      colorBands: [{ value: min, nextValue: max, color: constantColor, leftPercent: 0, widthPercent: 100 }],
      stepExpression: constantColor,
      useLinear: false,
      minColor: constantColor,
      maxColor: constantColor,
      isCategorical: false
    };
  }

  // Strings / categorical labels must use ``match``, not ``step`` (numeric stops only)
  if (uniqueValuesNeedMatchExpression(uniqueValues, layer.stats) && uniqueValues.length > 0) {
    const colors = paletteColors?.length ? paletteColors : [defaultColor];
    const categories = buildCategoriesFromUniqueValues(uniqueValues, colors);
    const categoricalBands = categories.map((category) => ({
      value: category.value,
      nextValue: category.value,
      color: category.color,
      leftPercent: 0,
      widthPercent: 0
    }));

    return {
      quantiles: uniqueValues,
      colorBands: categoricalBands,
      stepExpression: buildCategoryMatchExpression(categories, defaultColor, colorProperty),
      useLinear: false,
      minColor: null,
      maxColor: null,
      isCategorical: true,
      hideColorScale: false,
      categories
    };
  }

  // Compute class breaks and ensure they're sorted and unique (numeric only)
  let quantiles = resolveClassificationBreaks(layer, numStops, colorProperty);
  quantiles = [...new Set(quantiles)].sort((a, b) => a - b);

  // Get palette colors
  if (!paletteColors || paletteColors.length === 0) {
    // Fallback to default palette
    const defaultColors = getPaletteColors(getDefaultPalette(), false);
    const fallbackColor = defaultColors && defaultColors.length > 0 ? defaultColors[0] : '#440154';
    return {
      quantiles,
      colorBands: [],
      stepExpression: null,
      useLinear: true,
      minColor: fallbackColor,
      maxColor: fallbackColor
    };
  }

  // Single distinct value in the data
  if (uniqueValues.length === 1) {
    const sole = uniqueValues[0];
    const colorIndex = Math.floor(paletteColors.length / 2);
    const constantColor = paletteColors[colorIndex];
    return {
      quantiles: [sole, sole],
      colorBands: [{
        value: sole,
        nextValue: sole,
        color: constantColor,
        leftPercent: 0,
        widthPercent: 100
      }],
      stepExpression: constantColor,
      useLinear: false,
      minColor: constantColor,
      maxColor: constantColor,
      isCategorical: typeof sole === 'string'
    };
  }

  // Handle edge case: when all values are the same (min === max)
  // Use a single constant color for all features
  // Use tolerance-based comparison for floating point numbers to handle precision issues
  if (
    typeof min === 'number' &&
    typeof max === 'number' &&
    areValuesEffectivelyEqual(min, max)
  ) {
    // Use the middle color from the palette (or first if only one color)
    const colorIndex = Math.floor(paletteColors.length / 2);
    const constantColor = paletteColors[colorIndex];

    // Create a single color band for the legend
    const edgeCaseBands = [{
      value: min,
      nextValue: max,
      color: constantColor,
      leftPercent: 0,
      widthPercent: 100
    }];

    // Return a constant color expression (not linear interpolation)
    // This is more efficient and clearer than linear interpolation with same min/max
    return {
      quantiles: [min, max],
      colorBands: edgeCaseBands,
      stepExpression: constantColor, // Constant color for all features
      useLinear: false, // Not using linear interpolation, using constant color
      minColor: constantColor,
      maxColor: constantColor,
      isCategorical: false
    };
  }

  // Few unique numeric values: discrete ``step`` stops
  if (uniqueValues.length > 0 && uniqueValues.length < 10) {
    const categories = buildCategoriesFromUniqueValues(uniqueValues, paletteColors);
    let colorExpression = ['step', propertyGetExpression(colorProperty), defaultColor];
    categories.forEach((category) => {
      colorExpression.push(category.value);
      colorExpression.push(category.color);
    });
    if (!isValidMapLibreStepExpression(colorExpression)) {
      colorExpression = defaultColor;
    }

    const categoricalBands = categories.map((category) => ({
      value: category.value,
      nextValue: category.value,
      color: category.color,
      leftPercent: 0,
      widthPercent: 0
    }));

    return {
      quantiles: uniqueValues,
      colorBands: categoricalBands,
      stepExpression: colorExpression,
      useLinear: false,
      minColor: null,
      maxColor: null,
      isCategorical: false,
      categories
    };
  }

  // Handle edge case: if quantiles are invalid (but min !== max), use linear interpolation
  // But still create color bands for the legend
  if (quantiles.length < 2) {
    const minColor = paletteColors[0];
    const maxColor = paletteColors[paletteColors.length - 1];

    // Create a single color band for the legend
    const edgeCaseBands = [{
      value: min,
      nextValue: max,
      color: minColor,
      leftPercent: 0,
      widthPercent: 100
    }];

    return {
      quantiles: [min, max],
      colorBands: edgeCaseBands,
      stepExpression: null,
      useLinear: true,
      minColor,
      maxColor,
      isCategorical: false
    };
  }

  // Compute discrete color bands
  const numIntervals = quantiles.length - 1;
  const colorBands = [];
  const stepExpression = ['step', propertyGetExpression(colorProperty)];

  // Get default color for values below first quantile
  const firstColorIndex = mapIntervalToColorIndex(0, numIntervals, paletteColors.length);
  const firstColor = paletteColors[firstColorIndex];
  stepExpression.push(firstColor);

  // Calculate position and width for colorbar using linear scale
  // Since we're using equal-count quantiles, we use linear positioning based on min/max
  const range = max - min;

  // Create color bands and step expression stops
  for (let i = 0; i < numIntervals; i++) {
    const quantileValue = quantiles[i];
    const nextValue = quantiles[i + 1];

    // Skip if quantile value is invalid
    if (typeof quantileValue !== 'number' || isNaN(quantileValue) || !isFinite(quantileValue)) {
      continue;
    }

    // Skip if nextValue is invalid (required for color band calculation)
    if (typeof nextValue !== 'number' || isNaN(nextValue) || !isFinite(nextValue)) {
      continue;
    }

    // Clamp quantile values to safe range to prevent protobuf varint encoding errors
    // These values are used in MapLibre step expressions and must be within safe bounds
    const safeQuantileValue = clampToSafeRange(quantileValue);

    // Map interval index to palette color index
    const colorIndex = mapIntervalToColorIndex(i, numIntervals, paletteColors.length);
    const bandColor = paletteColors[colorIndex];

    // Add to step expression with clamped values
    stepExpression.push(safeQuantileValue);
    stepExpression.push(bandColor);

    // Use linear positioning for colorbar (use original values for display, not clamped)
    const leftPercent = range > 0 ? ((quantileValue - min) / range) * 100 : 0;
    const widthPercent = range > 0 ? ((nextValue - quantileValue) / range) * 100 : 100 / numIntervals;

    colorBands.push({
      value: quantileValue, // Keep original value for display
      nextValue: nextValue, // Keep original value for display
      color: bandColor,
      leftPercent: Math.max(0, Math.min(100, leftPercent)),
      widthPercent: Math.max(1, Math.min(100, widthPercent))
    });
  }

  let finalExpression = stepExpression;
  if (!isValidMapLibreStepExpression(finalExpression)) {
    finalExpression = firstColor;
  }

  return {
    quantiles,
    colorBands,
    stepExpression: finalExpression,
    useLinear: false,
    minColor: null,
    maxColor: null,
    isCategorical: false
  };
};

/**
 * Format numbers for display with simplified abbreviations (K, M, B)
 * Used consistently across legend and hover tooltips
 * @param {number|null|undefined} val - Value to format
 * @returns {string} Formatted value string
 */
export const formatValue = (val) => {
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'string') return val;

  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  // Simplify large numbers with abbreviations
  if (absVal >= 1e9) {
    return `${sign}${(absVal / 1e9).toFixed(4)} B`;
  } else if (absVal >= 1e6) {
    return `${sign}${(absVal / 1e6).toFixed(4)} M`;
  } else if (absVal >= 1) {
    return `${sign}${absVal.toFixed(1)}`;
  } else if (absVal >= 0.01) {
    return `${sign}${absVal.toFixed(4)}`;
  } else {
    // For very small numbers, use scientific notation
    return `${sign}${absVal.toExponential(1)}`;
  }
};

/** Map display: user-controlled cap on GeoJSON features sent to MapLibre per layer (localStorage). */
export const MAP_MAX_FEATURES_PER_LAYER_LS_KEY = 'manteo_map_max_features_per_layer';
export const MAP_MAX_FEATURES_PER_LAYER_MIN = 100;
export const MAP_MAX_FEATURES_PER_LAYER_MAX = 500000;
export const MAP_MAX_FEATURES_PER_LAYER_DEFAULT = 25000;
/** Above this, warn users that map rendering may slow the browser. */
export const MAP_MAX_FEATURES_PER_LAYER_SLOW_WARN = 50000;

export const parseMapMaxFeaturesPerLayer = (raw) => {
  if (raw == null || raw === '') return MAP_MAX_FEATURES_PER_LAYER_DEFAULT;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
  if (!Number.isFinite(n)) return MAP_MAX_FEATURES_PER_LAYER_DEFAULT;
  return Math.min(
    MAP_MAX_FEATURES_PER_LAYER_MAX,
    Math.max(MAP_MAX_FEATURES_PER_LAYER_MIN, Math.floor(n))
  );
};

/** Shallow-cloned FeatureCollection with at most `maxFeatures` features when over the limit. */
export const sliceGeoJsonForDisplay = (geojson, maxFeatures) => {
  if (!geojson?.features?.length) return geojson;
  const n = geojson.features.length;
  if (n <= maxFeatures) return geojson;
  return { ...geojson, features: geojson.features.slice(0, maxFeatures) };
};
